import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  IconButton,
  Paper,
  Grid,
  Avatar,
  CircularProgress,
  Card,
  CardContent,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  TablePagination,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import {
  Receipt,
  AccountBalance,
  Business,
  Person,
  Check,
  Cancel,
  Close,
  WatchLater,
  History,
  Refresh,
  Description,
  CheckCircle,
  Phone,
  Email,
  CloudUpload,
  GetApp,
  ArrowBack,
} from "@material-ui/icons";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import { connect, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import HttpService from "../../services/httpService";
import * as XLSX from "xlsx";
import { ROLENAME, constantDepartmentId } from "../../utils/index";
import { APPROVAL_TYPES } from "../../config/approvalTypesRegistry";
import ExternalApprovalQueue from "../../components/externalApprovalQueue";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    background: "linear-gradient(135deg, #1A3C6E 0%, #2E75B6 100%)",
    borderRadius: theme.spacing(2),
    color: "#fff",
    boxShadow: "0 8px 32px rgba(26, 60, 110, 0.3)",
  },
  headerTitle: {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  headerSubtitle: {
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
  },
  tabs: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: "3px 3px 0 0",
      backgroundColor: "#1A3C6E",
    },
  },
  tabLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 500,
  },
  badge: {
    marginLeft: theme.spacing(1),
    "& .MuiBadge-badge": {
      fontSize: "0.7rem",
      height: 20,
      minWidth: 20,
      borderRadius: 10,
    },
  },
  card: {
    borderRadius: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
    },
    marginBottom: theme.spacing(2),
    cursor: "pointer",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #f0f0f0",
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  statusChip: {
    fontWeight: 600,
    padding: theme.spacing(0.5, 1.5),
    "&.pending_approval": {
      backgroundColor: "#fff3e0",
      color: "#e65100",
    },
    "&.approved": {
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
    },
    "&.rejected": {
      backgroundColor: "#fbe9e7",
      color: "#c62828",
    },
    "&.paid": {
      backgroundColor: "#e3f2fd",
      color: "#0d47a1",
    },
  },
  approverAvatar: {
    width: 32,
    height: 32,
    fontSize: "0.75rem",
    backgroundColor: "#1A3C6E",
  },
  actionButton: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1, 3),
    fontWeight: 600,
    textTransform: "none",
    "&.approve": {
      backgroundColor: "#4caf50",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#388e3c",
      },
    },
    "&.reject": {
      backgroundColor: "#f44336",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#c62828",
      },
    },
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid #f5f5f5",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  infoLabel: {
    color: "#666",
    fontWeight: 500,
  },
  infoValue: {
    fontWeight: 500,
    color: "#333",
  },
  historyTimeline: {
    position: "relative",
    paddingLeft: theme.spacing(3),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 2,
      backgroundColor: "#e0e0e0",
    },
  },
  historyItem: {
    position: "relative",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#f8f9fa",
    borderRadius: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      left: -7,
      top: 16,
      width: 12,
      height: 12,
      borderRadius: "50%",
      backgroundColor: "#1A3C6E",
      border: "2px solid #fff",
    },
    "&.approved::before": {
      backgroundColor: "#4caf50",
    },
    "&.rejected::before": {
      backgroundColor: "#f44336",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: "#999",
  },
  loadingOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    alignItems: "center",
  },
  amountChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 12,
  },
  myRequestBadge: {
    backgroundColor: "#e3f2fd",
    color: "#0d47a1",
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: "0.7rem",
    fontWeight: 600,
  },
  docLink: {
    color: "#1A3C6E",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(2, 0),
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

// ─── Payment Type Constants ─────────────────────────────────────────────────
const PAYMENT_TYPE_ICONS = {
  rent: AccountBalance,
  housekeeping: Business,
  stationary: Business,
  porter: Person,
  ccd: Business,
  drinking_water: Business,
  marketing: Business,
  branding: Business,
  others: Business,
};

const PAYMENT_TYPE_LABELS = {
  rent: "Rent",
  housekeeping: "Housekeeping",
  stationary: "Stationary",
  porter: "Porter",
  ccd: "CCD",
  drinking_water: "Drinking Water",
  marketing: "Marketing",
  branding: "Branding",
  others: "Others",
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────
const MiscpaymentMasterDashboard = function (props) {
  const classes = useStyles();
  const history = useHistory();
  
  const [loading, setLocalLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [payments, setPayments] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [tdsPercentage, setTdsPercentage] = useState("");
  const [viewType, setViewType] = useState("all"); // Only for Pending tab: "all", "my"
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");

  const [externalTypeFilter, setExternalTypeFilter] = useState("all");
  const [otherPaymentTypeActive, setOtherPaymentTypeActive] = useState(false);

  const { userDetails } = useSelector((state) => state.user);
  const isBelongToFinance = userDetails?.departmentId === constantDepartmentId.FinanceDepartment;
  const isAdmin = !!userDetails?.roles?.find((role) => role?.name === ROLENAME.Admin);
  const canViewOtherPaymentType = isBelongToFinance || isAdmin;
  const showOtherPaymentType = otherPaymentTypeActive && canViewOtherPaymentType;
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    paid: 0,
    total: 0,
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // ── Get Current User ID ──────────────────────────────────────────────────
  const currentUserId = props.user?._id || JSON.parse(localStorage.getItem('user'))?._id;

  // ── Fetch Payments based on tab and filters ─────────────────────────────
  const fetchPayments = useCallback(async () => {
    try {
      setLocalLoading(true);
      let response;
      const params = {
        payment_type: paymentTypeFilter !== "all" ? paymentTypeFilter : undefined,
        page: page + 1,
        limit: rowsPerPage,
      };

      if (tabValue === 0) {
        // Pending tab - show payments where user is approver
        // Apply viewType filter for pending tab only
        if (viewType === "my") {
          response = await HttpService.getMyPaymentsForMiscPayment(params);
        } else {
          response = await HttpService.getPendingPaymentsForMiscPayment(params);
        }
      } else {
        // History tab - show approved/rejected/paid payments (no viewType filter)
        response = await HttpService.getPaymentHistoryForMiscPayment(params);
      }
      
      const result = response?.data?.data || {};
      const data = result?.payments || [];
      const pagination = result?.pagination || {};
      const statusCounts = result?.statusCounts || {};

      setPayments(data);
      setTotalCount(pagination?.total || 0);

      // Update stats — pending count comes from the pending endpoint's total
      // (scoped to the current view type), history counts come from the
      // history endpoint's statusCounts (computed across all pages, not
      // just the current page), so the cards stay accurate independent of
      // pagination/rowsPerPage.
      if (tabValue === 0) {
        setStats(prev => ({
          ...prev,
          pending: pagination?.total || 0,
        }));
      } else {
        setStats(prev => ({
          ...prev,
          approved: statusCounts.approved || 0,
          rejected: statusCounts.rejected || 0,
          paid: statusCounts.paid || 0,
          total: pagination?.total || 0,
        }));
      }
    } catch (err) {
      props.setSnackInfo("Failed to fetch payments", "error");
    } finally {
      setLocalLoading(false);
    }
  }, [tabValue, viewType, paymentTypeFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // ─── Handle Approval Action ─────────────────────────────────────────────
  const handleApprovalAction = async () => {
    if (!selectedPayment) return;

    try {
      setLocalLoading(true);
      const payload = {
        action: actionType,
        remarks: actionType === "rejected" ? remarks : remarks || "Approved",
      };
      if (actionType === "approved" && tdsPercentage !== "") {
        payload.tds_percentage = parseFloat(tdsPercentage);
      }

      const res = await HttpService.processApprovalForMiscPayment(selectedPayment._id, payload);

      if (res?.data?.success) {
        props.setSnackInfo(
          actionType === "approved"
            ? "Payment approved successfully!"
            : "Payment rejected successfully!",
          "success"
        );
        setActionDialogOpen(false);
        setRemarks("");
        setTdsPercentage("");
        fetchPayments();
      }
    } catch (err) {
      props.setSnackInfo(
        err?.response?.data?.message || "Failed to process approval",
        "error"
      );
    } finally {
      setLocalLoading(false);
    }
  };

  // ─── Last 3 months' amount history for the linked owner/provider ───────
  const [amountHistory, setAmountHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchAmountHistory = async (payment) => {
    const linkedOwnerId = payment?.linked_owner?._id || payment?.linked_owner;
    const linkedProviderId = payment?.linked_service_provider?._id || payment?.linked_service_provider;
    if (!linkedOwnerId && !linkedProviderId) {
      setAmountHistory([]);
      return;
    }
    try {
      setLoadingHistory(true);
      const res = await HttpService.getPaymentHistoryForMiscPayment({
        linked_owner: linkedOwnerId || undefined,
        linked_service_provider: linkedProviderId || undefined,
        payment_type: payment.payment_type,
        status: "approved",
        limit: 3,
      });
      const rows = (res?.data?.data?.payments || []).filter((p) => p._id !== payment._id);
      setAmountHistory(rows);
    } catch (err) {
      setAmountHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ─── Export the currently loaded rows to an Excel file (client-side) ───
  const handleExportExcel = () => {
    const rows = payments.map((p) => ({
      "Type": PAYMENT_TYPE_LABELS[p.payment_type] || p.payment_type,
      "Vendor / Owner": p.linked_owner?.owner_name || p.linked_service_provider?.name || p.payee_name || "—",
      "Experience Centre": p.experienceCenterId?.name || "—",
      "Month": p.payment_period_label || p.payment_month || "—",
      "Amount": p.amount,
      "GST": p.gst_amount || 0,
      "TDS": p.tds_amount || 0,
      "Net Payable": p.net_payable || p.amount,
      "Status": p.status,
      "Raised By": p.raised_by?.name || "—",
      "Raised On": p.created_at ? new Date(p.created_at).toLocaleDateString() : "—",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payments");
    XLSX.writeFile(workbook, `MiscPayments_${tabValue === 0 ? "Pending" : "History"}_${Date.now()}.xlsx`);
  };

  // ─── View Payment Details ──────────────────────────────────────────────
  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setDialogOpen(true);
    fetchAmountHistory(payment);
  };

  // ─── Open Action Dialog ────────────────────────────────────────────────
  const handleOpenAction = (payment, action) => {
    setSelectedPayment(payment);
    setActionType(action);
    setRemarks("");
    setTdsPercentage("");
    setActionDialogOpen(true);
  };

  // ─── Handle Page Change ──────────────────────────────────────────────────
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ─── Render Payment Card ────────────────────────────────────────────────
  const renderPaymentCard = (payment) => {
    const isPending = payment.status === "pending_approval";
    const isApproved = payment.status === "approved";
    const isRejected = payment.status === "rejected";
    const isPaid = payment.status === "paid";
    const isMyRequest = payment.raised_by?._id === currentUserId;

    const PaymentIcon = PAYMENT_TYPE_ICONS[payment.payment_type] || Receipt;
    const paymentTypeLabel = PAYMENT_TYPE_LABELS[payment.payment_type] || payment.payment_type;

    const payeeName = payment.linked_owner?.owner_name || 
                      payment.linked_service_provider?.name || 
                      payment.payee_name || 
                      "N/A";

    return (
      <Card className={classes.card} onClick={() => handleViewPayment(payment)}>
        <div className={classes.cardHeader}>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <PaymentIcon style={{ fontSize: 20, color: "#1A3C6E" }} />
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {paymentTypeLabel}
              </Typography>
              {isMyRequest && (
                <span className={classes.myRequestBadge}>My Request</span>
              )}
              {payment.payment_mode === "manual" && (
                <Chip
                  size="small"
                  label="Manual"
                  style={{ backgroundColor: "#FFF3E0", color: "#E65100", fontSize: 10 }}
                />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                size="small"
                label={payment.experienceCenterId?.name || "No EC"}
                variant="outlined"
                style={{ fontSize: "0.7rem" }}
              />
              <Chip
                size="small"
                label={payment.payment_mode?.toUpperCase() || "ERP"}
                variant="outlined"
                style={{ fontSize: "0.7rem" }}
              />
              <Chip
                size="small"
                label={payment.status?.replace("_", " ").toUpperCase()}
                className={`${classes.statusChip} ${payment.status}`}
              />
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={4}>
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Typography variant="h6" className={classes.amountChip}>
                ₹{(payment.net_payable ?? payment.amount)?.toLocaleString()}
              </Typography>
              {(payment.gst_amount > 0 || payment.tds_amount > 0) && (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 2 }}>
                  ₹{payment.amount?.toLocaleString()}
                  {payment.gst_amount > 0 ? ` + GST ₹${payment.gst_amount.toLocaleString()}` : ""}
                  {payment.tds_amount > 0 ? ` − TDS ₹${payment.tds_amount.toLocaleString()}` : ""}
                </Typography>
              )}
            </Box>
            {isPending && (
              <Box display="flex" gap={2} alignItems="center">
                <Button
                  size="small"
                  className={`${classes.actionButton} approve`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAction(payment, "approved");
                  }}
                  startIcon={<Check />}
                  style={{ marginRight: 8 , marginLeft : 8}} 
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  className={`${classes.actionButton} reject`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAction(payment, "rejected");
                  }}
                  startIcon={<Cancel />}
                >
                  Reject
                </Button>
              </Box>
            )}
          </Box>
        </div>
        <div className={classes.cardContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2">{payeeName}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Description fontSize="small" color="action" />
                <Typography variant="body2" noWrap>
                  {payment.description || "No description"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2">
                  Raised by: {payment.raised_by?.name || "Unknown"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Receipt fontSize="small" color="action" />
                <Typography variant="body2">
                  {payment.documents?.length || 0} document(s)
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {isPending && payment.currentApprovers?.length > 0 && (
            <Box mt={2} pt={2} borderTop="1px solid #f0f0f0">
              <Typography variant="caption" color="textSecondary">
                Current Approvers:
              </Typography>
              <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                {payment.currentApprovers.map((approver, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    avatar={<Avatar className={classes.approverAvatar}>
                      {approver.name?.charAt(0) || "A"}
                    </Avatar>}
                    label={approver.name || "Unknown"}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {isApproved && payment.bankStatus && (
            <Box mt={2} pt={2} borderTop="1px solid #f0f0f0">
              <Typography variant="caption" color="textSecondary">
                Bank Status: 
              </Typography>
              <Chip size="small" label={payment.bankStatus} style={{ marginLeft: 8 }} />
            </Box>
          )}

          {payment.payment_mode === "manual" && (
            <Box mt={2} pt={2} borderTop="1px solid #f0f0f0">
              <Typography variant="caption" color="textSecondary">
                Payment Method: {payment.external_payment_method?.toUpperCase() || "N/A"}
              </Typography>
            </Box>
          )}
        </div>
      </Card>
    );
  };

  // ─── Render Dialog ─────────────────────────────────────────────────────
  const renderDialog = () => (
    <Dialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" style={{ fontWeight: 600 }}>Payment Details</Typography>
          <IconButton onClick={() => setDialogOpen(false)}><Close /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {selectedPayment && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                <Receipt fontSize="small" /> Payment Information
              </Typography>
              <Divider style={{ margin: "8px 0 16px" }} />
              <Box>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Payment Type</span>
                  <span className={classes.infoValue}>
                    {PAYMENT_TYPE_LABELS[selectedPayment.payment_type] || selectedPayment.payment_type}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Payment Mode</span>
                  <span className={classes.infoValue}>
                    {selectedPayment.payment_mode?.toUpperCase()}
                    {selectedPayment.payment_mode === "manual" && (
                      <Chip size="small" label={selectedPayment.external_payment_method} style={{ marginLeft: 8 }} />
                    )}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Amount</span>
                  <span className={classes.infoValue}>₹{selectedPayment.amount?.toLocaleString()}</span>
                </div>
                {selectedPayment.payment_lines?.length > 1 && (
                  <Box mt={1} mb={1} p={1.5} style={{ background: "#f8f9fa", borderRadius: 8 }}>
                    <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                      Amount Breakup ({selectedPayment.payment_lines.length} rows)
                    </Typography>
                    {selectedPayment.payment_lines.map((line, idx) => (
                      <Box key={line._id || idx} display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="textSecondary">Row {idx + 1}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          ₹{(line.amount || 0).toLocaleString()}
                          {line.gst_percentage ? ` + ${line.gst_percentage}% GST (₹${(line.gst_amount || 0).toLocaleString()})` : ""}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>GST / TDS</span>
                  <span className={classes.infoValue}>
                    +₹{(selectedPayment.gst_amount || 0).toLocaleString()} / -₹{(selectedPayment.tds_amount || 0).toLocaleString()}
                  </span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Net Payable</span>
                  <span className={classes.infoValue}>
                    ₹{(selectedPayment.net_payable ?? selectedPayment.amount)?.toLocaleString()}
                  </span>
                </div>
                {(loadingHistory || amountHistory.length > 0) && (
                  <Box mt={1} mb={1} p={1.5} style={{ background: "#f8f9fa", borderRadius: 8 }}>
                    <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                      Last 3 Months — {PAYMENT_TYPE_LABELS[selectedPayment.payment_type] || selectedPayment.payment_type}
                    </Typography>
                    {loadingHistory ? (
                      <CircularProgress size={16} />
                    ) : (
                      amountHistory.map((h) => (
                        <Typography key={h._id} variant="caption" color="textSecondary" display="block">
                          {h.payment_period_label || h.payment_month || new Date(h.created_at).toLocaleDateString()}: ₹{h.amount?.toLocaleString()}
                        </Typography>
                      ))
                    )}
                  </Box>
                )}
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Status</span>
                  <Chip
                    size="small"
                    label={selectedPayment.status?.toUpperCase()}
                    className={`${classes.statusChip} ${selectedPayment.status}`}
                  />
                </div>
                {selectedPayment.payment_month && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Payment Month</span>
                    <span className={classes.infoValue}>
                      {new Date(selectedPayment.payment_month + "-01").toLocaleString('default', { 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                )}
                {selectedPayment.description && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Description</span>
                    <span className={classes.infoValue}>{selectedPayment.description}</span>
                  </div>
                )}
                {selectedPayment.remarks && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Remarks</span>
                    <span className={classes.infoValue}>{selectedPayment.remarks}</span>
                  </div>
                )}
              </Box>
            </Grid>

            {/* Linked Record / Payee Details */}
            {(selectedPayment.linked_owner || 
              selectedPayment.linked_service_provider || 
              selectedPayment.payee_name ||
              selectedPayment.payee_email ||
              selectedPayment.payee_number) && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  <Person fontSize="small" /> {selectedPayment.payment_mode === "manual" ? "Payee Details" : "Linked Record"}
                </Typography>
                <Divider style={{ margin: "8px 0 16px" }} />
                <Box>
                  {selectedPayment.linked_owner && (
                    <>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>Owner Name</span>
                        <span className={classes.infoValue}>{selectedPayment.linked_owner.owner_name}</span>
                      </div>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>PAN</span>
                        <span className={classes.infoValue}>{selectedPayment.linked_owner.pan_number}</span>
                      </div>
                    </>
                  )}
                  {selectedPayment.linked_service_provider && (
                    <>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>Provider Name</span>
                        <span className={classes.infoValue}>{selectedPayment.linked_service_provider.name}</span>
                      </div>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>Service Type</span>
                        <span className={classes.infoValue}>
                          {(selectedPayment.linked_service_provider.location_mappings || [])
                            .flatMap((m) => m.scopes || [])
                            .map((s) => (s.service_type === "others" ? s.service_type_other : s.service_type))
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </span>
                      </div>
                    </>
                  )}
                  {selectedPayment.payee_name && (
                    <div className={classes.infoRow}>
                      <span className={classes.infoLabel}>Payee Name</span>
                      <span className={classes.infoValue}>{selectedPayment.payee_name}</span>
                    </div>
                  )}
                  {selectedPayment.payee_email && (
                    <div className={classes.infoRow}>
                      <span className={classes.infoLabel}>Payee Email</span>
                      <span className={classes.infoValue}>{selectedPayment.payee_email}</span>
                    </div>
                  )}
                  {selectedPayment.payee_number && (
                    <div className={classes.infoRow}>
                      <span className={classes.infoLabel}>Payee Mobile</span>
                      <span className={classes.infoValue}>{selectedPayment.payee_number}</span>
                    </div>
                  )}
                </Box>
              </Grid>
            )}

            {/* Bank Details - Only for ERP payments */}
            {selectedPayment.bank && selectedPayment.payment_mode === "erp" && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  <AccountBalance fontSize="small" /> Bank Details
                </Typography>
                <Divider style={{ margin: "8px 0 16px" }} />
                <Box>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Account Number</span>
                    <span className={classes.infoValue}>{selectedPayment.bank.account_number}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Account Name</span>
                    <span className={classes.infoValue}>{selectedPayment.bank.account_name}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Bank / IFSC</span>
                    <span className={classes.infoValue}>
                      {selectedPayment.bank.bank_name} - {selectedPayment.bank.ifsc_code}
                    </span>
                  </div>
                </Box>
              </Grid>
            )}

            {/* Documents */}
            {selectedPayment.documents?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  <CloudUpload fontSize="small" /> Documents ({selectedPayment.documents.length})
                </Typography>
                <Divider style={{ margin: "8px 0 16px" }} />
                <Box>
                  {selectedPayment.documents.map((doc, idx) => (
                    <div key={idx} className={classes.infoRow}>
                      <span className={classes.infoLabel}>Document {idx + 1}</span>
                      <span className={classes.infoValue}>
                        <a 
                          href={doc.file_path} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={classes.docLink}
                        >
                          {doc.original_name || doc.file_path?.split('/').pop() || `Document ${idx + 1}`}
                        </a>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {(doc.file_size_kb || 0).toFixed(0)} KB · {doc.mime_type || 'Unknown type'}
                        </Typography>
                      </span>
                    </div>
                  ))}
                </Box>
              </Grid>
            )}

            {/* Approval History */}
            {selectedPayment.approvalHistory?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  <History fontSize="small" /> Approval History
                </Typography>
                <Divider style={{ margin: "8px 0 16px" }} />
                <Box className={classes.historyTimeline}>
                  {selectedPayment.approvalHistory.map((item, idx) => (
                    <div key={idx} className={`${classes.historyItem} ${item.action}`}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          Level {item.level} - {item.action?.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(item.actedAt).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        By: {item.actedBy?.name || "Unknown"}
                      </Typography>
                      {item.remarks && (
                        <Typography variant="caption" style={{ color: "#666" }}>
                          Remarks: {item.remarks}
                        </Typography>
                      )}
                    </div>
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      {selectedPayment?.status === "pending_approval" && (
        <DialogActions style={{ padding: theme => theme.spacing(2, 3) }}>
          <Button
            variant="contained"
            className={`${classes.actionButton} reject`}
            onClick={() => {
              setDialogOpen(false);
              handleOpenAction(selectedPayment, "rejected");
            }}
            startIcon={<Cancel />}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            className={`${classes.actionButton} approve`}
            onClick={() => {
              setDialogOpen(false);
              handleOpenAction(selectedPayment, "approved");
            }}
            startIcon={<Check />}
          >
            Approve
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );

  // ─── Render Action Dialog ──────────────────────────────────────────────
  const renderActionDialog = () => (
    <Dialog
      open={actionDialogOpen}
      onClose={() => setActionDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {actionType === "approved" ? (
            <CheckCircle style={{ color: "#4caf50" }} />
          ) : (
            <Cancel style={{ color: "#f44336" }} />
          )}
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {actionType === "approved" ? "Approve" : "Reject"} Payment
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {actionType === "approved"
            ? "Are you sure you want to approve this payment? This action will move it to the next approval level."
            : "Please provide a reason for rejecting this payment."}
        </Typography>
        {actionType === "rejected" && (
          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            label="Remarks *"
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Please explain why this payment is being rejected..."
            helperText="Remarks are required for rejection"
            error={actionType === "rejected" && !remarks.trim()}
          />
        )}
        {actionType === "approved" && (
          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            label="Remarks (Optional)"
            multiline
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add any additional comments..."
          />
        )}
        {actionType === "approved" && (
          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            label="TDS % (Optional)"
            type="number"
            inputProps={{ min: 0, max: 100, step: 0.5 }}
            value={tdsPercentage}
            onChange={(e) => setTdsPercentage(e.target.value)}
            placeholder="Leave blank to keep as-is"
            helperText="Deducted from the net payable amount before this goes to the bank"
          />
        )}
        {selectedPayment && (() => {
          const baseAmount = selectedPayment.amount || 0;
          const gstAmount = selectedPayment.gst_amount || 0;
          const previewing = actionType === "approved" && tdsPercentage !== "";
          const previewTdsPct = previewing ? (parseFloat(tdsPercentage) || 0) : (selectedPayment.tds_percentage || 0);
          const previewTdsAmount = previewing
            ? Math.round((baseAmount * previewTdsPct) / 100 * 100) / 100
            : (selectedPayment.tds_amount || 0);
          const previewNet = previewing
            ? Math.round((baseAmount + gstAmount - previewTdsAmount) * 100) / 100
            : (selectedPayment.net_payable ?? baseAmount);
          return (
          <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
            <Typography variant="caption" color="textSecondary" display="block">
              Payment Details
            </Typography>
            <Typography variant="body2">
              <strong>Amount:</strong> ₹{baseAmount.toLocaleString()}
            </Typography>
            {selectedPayment.payment_lines?.length > 1 && (
              <Box mt={0.5} mb={0.5} pl={1} style={{ borderLeft: "2px solid #e0e0e0" }}>
                {selectedPayment.payment_lines.map((line, idx) => (
                  <Typography key={line._id || idx} variant="caption" color="textSecondary" display="block">
                    Row {idx + 1}: ₹{(line.amount || 0).toLocaleString()}
                    {line.gst_percentage ? ` + ${line.gst_percentage}% GST (₹${(line.gst_amount || 0).toLocaleString()})` : ""}
                  </Typography>
                ))}
              </Box>
            )}
            <Typography variant="body2">
              <strong>GST / TDS:</strong> +₹{gstAmount.toLocaleString()} / -₹{previewTdsAmount.toLocaleString()}
            </Typography>
            <Typography variant="body2">
              <strong>Net Payable:</strong> ₹{previewNet.toLocaleString()}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {PAYMENT_TYPE_LABELS[selectedPayment.payment_type] || selectedPayment.payment_type}
            </Typography>
            <Typography variant="body2">
              <strong>Payee:</strong> {selectedPayment.linked_owner?.owner_name || 
                                       selectedPayment.linked_service_provider?.name || 
                                       selectedPayment.payee_name || "N/A"}
            </Typography>
          </Box>
          );
        })()}
      </DialogContent>
      <DialogActions style={{ padding: theme => theme.spacing(2, 3) }}>
        <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
        <Button
          variant="contained"
          className={`${classes.actionButton} ${actionType}`}
          onClick={handleApprovalAction}
          disabled={actionType === "rejected" && !remarks.trim()}
          startIcon={actionType === "approved" ? <Check /> : <Cancel />}
        >
          Confirm {actionType === "approved" ? "Approve" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ─── Main Render ──────────────────────────────────────────────────────
  return (
    <div className={classes.root}>
      {/* Header */}
      <Paper className={classes.header}>
        <Typography variant="h4" className={classes.headerTitle}>
          <Receipt fontSize="large" />
          Payment Approval Dashboard
        </Typography>
        <Typography variant="body1" className={classes.headerSubtitle}>
          Manage and review payment requests
        </Typography>
      </Paper>

      {!showOtherPaymentType && (
        <>
      {/* Stats Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Pending</Typography>
            <Typography variant="h4" style={{ color: "#ff9800", fontWeight: 600 }}>
              {stats.pending}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Approved</Typography>
            <Typography variant="h4" style={{ color: "#4caf50", fontWeight: 600 }}>
              {stats.approved}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Rejected</Typography>
            <Typography variant="h4" style={{ color: "#f44336", fontWeight: 600 }}>
              {stats.rejected}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Paid</Typography>
            <Typography variant="h4" style={{ color: "#0d47a1", fontWeight: 600 }}>
              {stats.paid}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper className={classes.tabs}>
        <Tabs
          value={tabValue}
          onChange={(e, val) => {
            setTabValue(val);
            setPage(0);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <div className={classes.tabLabel}>
                <WatchLater fontSize="small" />
                Pending
                <Badge badgeContent={stats.pending} color="secondary" className={classes.badge} />
              </div>
            }
          />
          <Tab
            label={
              <div className={classes.tabLabel}>
                <History fontSize="small" />
                History
                <Badge badgeContent={stats.approved + stats.rejected + stats.paid} color="primary" className={classes.badge} />
              </div>
            }
          />
        </Tabs>
      </Paper>
        </>
      )}

      {/* Filter Bar - View Type/Export/Refresh only apply to the native Misc
          Payment view; Other Payment Type is always available to switch views. */}
      <div className={classes.filterBar}>
        {!showOtherPaymentType && tabValue === 0 && (
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>View Type</InputLabel>
            <Select
              value={viewType}
              onChange={(e) => {
                setViewType(e.target.value);
                setPage(0);
              }}
              label="View Type"
            >
              <MenuItem value="all">All Requests</MenuItem>
              <MenuItem value="my">My Requests</MenuItem>
            </Select>
          </FormControl>
        )}

        {!showOtherPaymentType && (
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Misc Payment Type</InputLabel>
            <Select
              value={paymentTypeFilter}
              onChange={(e) => {
                setPaymentTypeFilter(e.target.value);
                setPage(0);
              }}
              label="Misc Payment Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              {Object.entries(PAYMENT_TYPE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {canViewOtherPaymentType && (
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Other Payment Type</InputLabel>
            <Select
              value={externalTypeFilter}
              onChange={(e) => {
                setExternalTypeFilter(e.target.value);
                setOtherPaymentTypeActive(true);
              }}
              label="Other Payment Type"
            >
              <MenuItem value="all">All</MenuItem>
              {APPROVAL_TYPES.map((t) => (
                <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {showOtherPaymentType && (
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={() => setOtherPaymentTypeActive(false)}
          >
            Back to Misc Payments
          </Button>
        )}

        {!showOtherPaymentType && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<GetApp />}
              onClick={handleExportExcel}
              disabled={payments.length === 0}
              style={{ marginLeft: 'auto' }}
            >
              Export to Excel
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchPayments}
            >
              Refresh
            </Button>
          </>
        )}
      </div>

      {/* Other Payment Type view — a completely different approval queue,
          kept structurally separate from the native Misc Payment cards below
          so the two never get visually mixed together. */}
      {showOtherPaymentType && <ExternalApprovalQueue typeKey={externalTypeFilter} />}

      {/* Content */}
      {!showOtherPaymentType && (loading ? (
        <div className={classes.loadingOverlay}>
          <CircularProgress />
        </div>
      ) : (
        <>
          {tabValue === 0 && (
            <>
              {payments.length === 0 ? (
                <Paper className={classes.emptyState}>
                  <CheckCircle style={{ fontSize: 60, color: "#4caf50", marginBottom: 16 }} />
                  <Typography variant="h6">No pending approvals</Typography>
                  <Typography variant="body2" color="textSecondary">
                    All payment requests are up to date.
                  </Typography>
                </Paper>
              ) : (
                payments.map((item) => renderPaymentCard(item))
              )}
            </>
          )}
          {tabValue === 1 && (
            <>
              {payments.length === 0 ? (
                <Paper className={classes.emptyState}>
                  <History style={{ fontSize: 60, color: "#999", marginBottom: 16 }} />
                  <Typography variant="h6">No history available</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Completed payment approvals will appear here.
                  </Typography>
                </Paper>
              ) : (
                payments.map((item) => renderPaymentCard(item))
              )}
            </>
          )}
        </>
      ))}

      {/* Pagination */}
      {!showOtherPaymentType && totalCount > 0 && (
        <div className={classes.paginationContainer}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[50, 100, 200, 500]}
            labelRowsPerPage="Rows per page:"
          />
        </div>
      )}

      {/* Dialogs */}
      {renderDialog()}
      {renderActionDialog()}
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(MiscpaymentMasterDashboard);